import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Building2, Check, X, Package, DollarSign, Users,
  Shield, PieChart, UserCheck, Search, Plus, Gift
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OFFER_TYPE_LABELS = {
  money: 'Money',
  merchandise: 'Merchandise',
  travel_sponsorship: 'Travel Sponsorship',
  equipment: 'Equipment',
  training_camp: 'Training Camp',
  scholarship: 'Scholarship',
  nutrition_support: 'Nutrition Support',
  medical_cover: 'Medical Cover',
  accommodation: 'Accommodation',
  mixed: 'Mixed / Combined',
};

const OFFER_TYPE_COLORS = {
  money: 'bg-green-500/20 text-green-400',
  merchandise: 'bg-blue-500/20 text-blue-400',
  travel_sponsorship: 'bg-purple-500/20 text-purple-400',
  equipment: 'bg-cyan-500/20 text-cyan-400',
  training_camp: 'bg-orange-500/20 text-orange-400',
  scholarship: 'bg-yellow-500/20 text-yellow-400',
  nutrition_support: 'bg-emerald-500/20 text-emerald-400',
  medical_cover: 'bg-red-500/20 text-red-400',
  accommodation: 'bg-pink-500/20 text-pink-400',
  mixed: 'bg-white/10 text-white/60',
};

const INDUSTRIES = [
  'Telecommunications', 'Sportswear', 'Banking & Finance', 'Beverages',
  'Insurance', 'Technology', 'Real Estate', 'Media', 'Healthcare',
  'Automotive', 'Energy', 'FMCG', 'Government', 'NGO', 'Other',
];

export default function SponsorshipTab({ token }) {
  const [corporates, setCorporates] = useState([]);
  const [packages, setPackages] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [sportReport, setSportReport] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [supportedSports, setSupportedSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [showSponsorDialog, setShowSponsorDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignCorporate, setAssignCorporate] = useState(null);
  const [assignPackageId, setAssignPackageId] = useState('');
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [contractSearch, setContractSearch] = useState('');
  const [contractSportFilter, setContractSportFilter] = useState('all');

  const [packageForm, setPackageForm] = useState({
    package_name: '', amount: '', currency_code: 'KES',
    platform_fee_percentage: 20, description: '', offer_type: 'money', target_sport: '',
  });

  const [sponsorForm, setSponsorForm] = useState({
    company_name: '', contact_person: '', contact_email: '',
    contact_phone: '', country: 'Kenya', industry: '',
    website: '', password: 'sponsor123',
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [corpRes, pkgRes, analyticsRes, reportRes, contractsRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/api/sponsorship/admin/corporates`, { headers }),
        axios.get(`${API_URL}/api/sponsorship/admin/packages`, { headers }),
        axios.get(`${API_URL}/api/sponsorship/admin/analytics`, { headers }),
        axios.get(`${API_URL}/api/sponsorship/admin/sport-report`, { headers }),
        axios.get(`${API_URL}/api/sponsorship/admin/contracts`, { headers }),
        axios.get(`${API_URL}/api/rankings/config`),
      ]);
      setCorporates(corpRes.data.corporates || []);
      setPackages(pkgRes.data.packages || []);
      setAnalytics(analyticsRes.data);
      setSportReport(reportRes.data.report || []);
      setContracts(contractsRes.data.contracts || []);
      setSupportedSports(configRes.data.supported_sports || []);
    } catch (err) {
      console.error('Failed to load sponsorship data:', err);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const reviewCorporate = async (id, status) => {
    try {
      await axios.post(`${API_URL}/api/sponsorship/admin/corporates/${id}/review`, { status }, { headers });
      toast.success(`Corporate ${status}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Review failed');
    }
  };

  const createPackage = async () => {
    if (!packageForm.package_name || !packageForm.amount) { toast.error('Fill required fields'); return; }
    try {
      await axios.post(`${API_URL}/api/sponsorship/admin/packages`, {
        ...packageForm,
        amount: parseFloat(packageForm.amount),
        active_status: true,
      }, { headers });
      toast.success('Package created');
      setShowPackageDialog(false);
      setPackageForm({ package_name: '', amount: '', currency_code: 'KES', platform_fee_percentage: 20, description: '', offer_type: 'money', target_sport: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create package');
    }
  };

  const addSponsor = async () => {
    if (!sponsorForm.company_name || !sponsorForm.contact_person || !sponsorForm.contact_email) {
      toast.error('Fill all required fields'); return;
    }
    try {
      await axios.post(`${API_URL}/api/sponsorship/register`, sponsorForm);
      toast.success(`${sponsorForm.company_name} registered!`);
      const corpRes = await axios.get(`${API_URL}/api/sponsorship/admin/corporates`, { headers });
      const newCorp = (corpRes.data.corporates || []).find(
        c => c.contact_email === sponsorForm.contact_email && c.status === 'pending'
      );
      if (newCorp) {
        await axios.post(`${API_URL}/api/sponsorship/admin/corporates/${newCorp.id}/review`, { status: 'approved' }, { headers });
        toast.success(`${sponsorForm.company_name} approved!`);
      }
      setShowSponsorDialog(false);
      setSponsorForm({ company_name: '', contact_person: '', contact_email: '', contact_phone: '', country: 'Kenya', industry: '', website: '', password: 'sponsor123' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to register sponsor');
    }
  };

  const assignPackage = async () => {
    if (!assignCorporate || !assignPackageId) { toast.error('Select a package'); return; }
    try {
      const res = await axios.post(`${API_URL}/api/sponsorship/admin/assign-package-action`, {
        corporate_id: assignCorporate.id,
        package_id: assignPackageId,
      }, { headers });
      toast.success(res.data.message);
      setShowAssignDialog(false);
      setAssignCorporate(null);
      setAssignPackageId('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign package');
    }
  };

  const openAssignDialog = (corp) => {
    setAssignCorporate(corp);
    setAssignPackageId('');
    setShowAssignDialog(true);
  };

  if (loading) return <div className="text-center py-8 text-gray-400">Loading sponsorship data...</div>;

  const pending = corporates.filter(c => c.status === 'pending');
  const approved = corporates.filter(c => c.status === 'approved');
  const uniqueSports = [...new Set(contracts.map(c => c.sport).filter(Boolean))];
  const filteredContracts = contracts.filter(c => {
    const matchSearch = !contractSearch ||
      (c.athlete_name || '').toLowerCase().includes(contractSearch.toLowerCase()) ||
      (c.company_name || '').toLowerCase().includes(contractSearch.toLowerCase());
    const matchSport = contractSportFilter === 'all' || c.sport === contractSportFilter;
    return matchSearch && matchSport;
  });
  const activePackages = packages.filter(p => p.active_status);

  return (
    <div className="space-y-6" data-testid="sponsorship-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Building2 className="w-5 h-5 text-orange-400" /> Sponsorship Management
        </h2>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowSponsorDialog(true)} className="border-white/10 text-white/70 hover:text-white" data-testid="btn-add-sponsor">
            <Plus className="w-4 h-4 mr-1" /> Add Sponsor
          </Button>
          <Button size="sm" onClick={() => setShowPackageDialog(true)} className="bg-orange-600 hover:bg-orange-700" data-testid="btn-create-package">
            <Package className="w-4 h-4 mr-1" /> Create Package
          </Button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-white/10 pb-0">
        {[
          { key: 'overview', label: 'Overview', icon: PieChart },
          { key: 'sponsored', label: 'Sponsored Athletes', icon: UserCheck },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setActiveSubTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === t.key ? 'border-orange-400 text-orange-400' : 'border-transparent text-white/40 hover:text-white/70'
            }`}
            data-testid={`subtab-${t.key}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Overview Sub-Tab */}
      {activeSubTab === 'overview' && (
        <>
          {analytics && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="sponsorship-analytics">
              {[
                { label: 'Total Corporates', value: analytics.total_corporates, icon: Building2, color: 'text-orange-400' },
                { label: 'Approved', value: analytics.approved_corporates, icon: Check, color: 'text-emerald-400' },
                { label: 'Pending Approval', value: analytics.pending_corporates, icon: Shield, color: 'text-yellow-400' },
                { label: 'Total Sponsorship', value: `${(analytics.total_sponsorship_value || 0).toLocaleString()}`, icon: DollarSign, color: 'text-green-400' },
                { label: 'Athletes Sponsored', value: analytics.athletes_sponsored, icon: Users, color: 'text-blue-400' },
              ].map((s, i) => (
                <div key={i} className="bg-white/[0.03] border border-white/5 rounded-lg p-3">
                  <s.icon className={`w-4 h-4 ${s.color} mb-1`} />
                  <p className="text-xl font-bold text-white">{s.value}</p>
                  <p className="text-[10px] text-white/40 uppercase tracking-wider">{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Pending Approvals */}
          {pending.length > 0 && (
            <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-4">
              <h3 className="text-sm font-medium text-yellow-400 mb-3 flex items-center gap-2"><Shield className="w-4 h-4" /> Pending Approvals ({pending.length})</h3>
              <div className="space-y-2">
                {pending.map(c => (
                  <div key={c.id} className="flex items-center justify-between bg-white/5 rounded p-3" data-testid={`pending-corp-${c.id}`}>
                    <div>
                      <span className="text-white font-medium text-sm">{c.company_name}</span>
                      <span className="text-white/30 text-xs ml-2">{c.contact_email} — {c.industry || 'N/A'}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => reviewCorporate(c.id, 'approved')} className="bg-emerald-600 hover:bg-emerald-700">
                        <Check className="w-3 h-3 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => reviewCorporate(c.id, 'rejected')} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                        <X className="w-3 h-3 mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Approved Corporates with Assign Package */}
          <div className="bg-white/[0.02] border border-white/5 rounded-lg p-4">
            <h3 className="text-sm font-medium text-white/60 mb-3">Approved Corporates ({approved.length})</h3>
            {approved.length === 0 ? (
              <p className="text-white/30 text-sm text-center py-4">No approved corporates yet.</p>
            ) : (
              <div className="space-y-2" data-testid="approved-corporates">
                {approved.map(c => (
                  <div key={c.id} className="flex items-center justify-between bg-white/5 rounded p-3">
                    <div className="flex-1 min-w-0">
                      <span className="text-white font-medium text-sm">{c.company_name}</span>
                      <span className="text-white/30 text-xs ml-2">{c.contact_person} — {c.contact_email}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {c.industry && <span className="text-xs bg-white/5 text-white/40 px-2 py-0.5 rounded hidden md:inline">{c.industry}</span>}
                      {c.current_package && <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded">{c.current_package}</span>}
                      <span className="text-emerald-400 text-xs font-medium">{(c.total_budget || 0).toLocaleString()} KES</span>
                      <Button size="sm" variant="outline" onClick={() => openAssignDialog(c)} className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 h-7 text-xs" data-testid={`assign-pkg-${c.id}`}>
                        <Gift className="w-3 h-3 mr-1" /> Assign Package
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Packages */}
          <div className="bg-white/[0.02] border border-white/5 rounded-lg p-4">
            <h3 className="text-sm font-medium text-white/60 mb-3 flex items-center gap-2"><Package className="w-4 h-4 text-blue-400" /> Sponsorship Packages</h3>
            <div className="grid md:grid-cols-3 gap-3" data-testid="admin-packages">
              {packages.map(p => (
                <div key={p.id} className="bg-white/5 rounded p-4 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-bold text-sm">{p.package_name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${p.active_status ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>{p.active_status ? 'Active' : 'Inactive'}</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{p.amount?.toLocaleString()} <span className="text-sm text-white/40">{p.currency_code}</span></p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {p.offer_type && (
                      <span className={`text-xs px-2 py-0.5 rounded ${OFFER_TYPE_COLORS[p.offer_type] || 'bg-white/10 text-white/60'}`}>
                        {OFFER_TYPE_LABELS[p.offer_type] || p.offer_type}
                      </span>
                    )}
                    {p.target_sport && (
                      <span className="text-xs bg-cyan-500/15 text-cyan-400 px-2 py-0.5 rounded">{p.target_sport}</span>
                    )}
                  </div>
                  <p className="text-white/30 text-xs mt-1">Fee: {p.platform_fee_percentage}%</p>
                  {p.description && <p className="text-white/20 text-xs mt-1">{p.description}</p>}
                </div>
              ))}
            </div>
          </div>

          {/* Sport Report */}
          {sportReport.length > 0 && (
            <div className="bg-white/[0.02] border border-white/5 rounded-lg p-4">
              <h3 className="text-sm font-medium text-white/60 mb-3 flex items-center gap-2"><PieChart className="w-4 h-4 text-purple-400" /> Sport-wise Report</h3>
              <table className="w-full" data-testid="sport-report-table">
                <thead>
                  <tr className="border-b border-white/5 text-white/30 text-xs">
                    <th className="px-4 py-2 text-left">Sport</th>
                    <th className="px-4 py-2 text-right">Amount</th>
                    <th className="px-4 py-2 text-right">Athletes</th>
                  </tr>
                </thead>
                <tbody>
                  {sportReport.map(r => (
                    <tr key={r._id} className="border-b border-white/5">
                      <td className="px-4 py-2 text-white text-sm">{r._id}</td>
                      <td className="px-4 py-2 text-right text-emerald-400 font-bold text-sm">{r.sponsorship_amount?.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right text-white/50 text-sm">{r.athletes_sponsored}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Sponsored Athletes Sub-Tab */}
      {activeSubTab === 'sponsored' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input placeholder="Search athlete or sponsor..." value={contractSearch} onChange={e => setContractSearch(e.target.value)} className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="contract-search" />
            </div>
            <Select value={contractSportFilter} onValueChange={setContractSportFilter}>
              <SelectTrigger className="w-[180px] bg-white/5 border-white/10 text-white" data-testid="contract-sport-filter">
                <SelectValue placeholder="All Sports" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sports</SelectItem>
                {uniqueSports.map(s => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
              </SelectContent>
            </Select>
            <span className="text-xs text-white/40">{filteredContracts.length} contract{filteredContracts.length !== 1 ? 's' : ''}</span>
          </div>
          {filteredContracts.length === 0 ? (
            <div className="text-center py-12 bg-white/[0.02] border border-white/5 rounded-lg">
              <UserCheck className="w-10 h-10 text-white/10 mx-auto mb-2" />
              <p className="text-white/40 text-sm">No sponsorship contracts found</p>
            </div>
          ) : (
            <div className="bg-white/[0.02] border border-white/5 rounded-lg overflow-hidden">
              <table className="w-full" data-testid="sponsored-athletes-table">
                <thead>
                  <tr className="border-b border-white/5 text-white/30 text-xs uppercase tracking-wider">
                    <th className="px-4 py-3 text-left">Athlete</th>
                    <th className="px-4 py-3 text-left">Sponsor</th>
                    <th className="px-4 py-3 text-left">Sport</th>
                    <th className="px-4 py-3 text-left">Category</th>
                    <th className="px-4 py-3 text-right">Value</th>
                    <th className="px-4 py-3 text-center">Status</th>
                    <th className="px-4 py-3 text-left hidden lg:table-cell">Period</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContracts.map(c => (
                    <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3"><span className="text-white font-medium text-sm">{c.athlete_name || 'Unknown'}</span></td>
                      <td className="px-4 py-3"><span className="text-orange-400 text-sm">{c.company_name || 'Unknown'}</span></td>
                      <td className="px-4 py-3"><span className="text-xs bg-white/5 text-white/60 px-2 py-0.5 rounded">{c.sport}</span></td>
                      <td className="px-4 py-3"><span className="text-xs text-white/50 capitalize">{(c.category || '').replace(/_/g, ' ')}</span></td>
                      <td className="px-4 py-3 text-right"><span className="text-emerald-400 font-bold text-sm">{(c.contract_value || 0).toLocaleString()} <span className="text-white/30 text-xs">{c.currency || 'KES'}</span></span></td>
                      <td className="px-4 py-3 text-center"><span className={`text-xs px-2 py-0.5 rounded ${c.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 text-white/40'}`}>{c.status}</span></td>
                      <td className="px-4 py-3 hidden lg:table-cell"><span className="text-white/30 text-xs">{c.start_date ? new Date(c.start_date).toLocaleDateString() : '—'} — {c.end_date ? new Date(c.end_date).toLocaleDateString() : '—'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create Package Dialog */}
      <Dialog open={showPackageDialog} onOpenChange={setShowPackageDialog}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5 text-blue-400" /> Create Package</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Package Name *" value={packageForm.package_name} onChange={e => setPackageForm(p => ({ ...p, package_name: e.target.value }))} className="bg-white/5 border-white/10 text-white" data-testid="pkg-name-input" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/60 text-xs mb-1 block">Offering Type *</Label>
                <Select value={packageForm.offer_type} onValueChange={v => setPackageForm(p => ({ ...p, offer_type: v }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="pkg-offer-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(OFFER_TYPE_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/60 text-xs mb-1 block">Target Sport</Label>
                <Select value={packageForm.target_sport || 'any'} onValueChange={v => setPackageForm(p => ({ ...p, target_sport: v === 'any' ? '' : v }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="pkg-sport-select">
                    <SelectValue placeholder="Any Sport" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    <SelectItem value="any">Any Sport</SelectItem>
                    {supportedSports.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" placeholder="Amount *" value={packageForm.amount} onChange={e => setPackageForm(p => ({ ...p, amount: e.target.value }))} className="bg-white/5 border-white/10 text-white" data-testid="pkg-amount-input" />
              <Input type="number" placeholder="Fee %" value={packageForm.platform_fee_percentage} onChange={e => setPackageForm(p => ({ ...p, platform_fee_percentage: parseFloat(e.target.value) || 20 }))} className="bg-white/5 border-white/10 text-white" />
            </div>
            <Input placeholder="Description" value={packageForm.description} onChange={e => setPackageForm(p => ({ ...p, description: e.target.value }))} className="bg-white/5 border-white/10 text-white" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPackageDialog(false)} className="border-white/10 text-white/60">Cancel</Button>
            <Button onClick={createPackage} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-save-package">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Sponsor Dialog */}
      <Dialog open={showSponsorDialog} onOpenChange={setShowSponsorDialog}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-orange-400" /> Add New Sponsor</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Company Name *" value={sponsorForm.company_name} onChange={e => setSponsorForm(p => ({ ...p, company_name: e.target.value }))} className="bg-white/5 border-white/10 text-white" data-testid="sponsor-company-input" />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Contact Person *" value={sponsorForm.contact_person} onChange={e => setSponsorForm(p => ({ ...p, contact_person: e.target.value }))} className="bg-white/5 border-white/10 text-white" data-testid="sponsor-person-input" />
              <Input placeholder="Contact Email *" type="email" value={sponsorForm.contact_email} onChange={e => setSponsorForm(p => ({ ...p, contact_email: e.target.value }))} className="bg-white/5 border-white/10 text-white" data-testid="sponsor-email-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Phone" value={sponsorForm.contact_phone} onChange={e => setSponsorForm(p => ({ ...p, contact_phone: e.target.value }))} className="bg-white/5 border-white/10 text-white" />
              <Input placeholder="Website" value={sponsorForm.website} onChange={e => setSponsorForm(p => ({ ...p, website: e.target.value }))} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/60 text-xs mb-1 block">Industry</Label>
                <Select value={sponsorForm.industry || 'none'} onValueChange={v => setSponsorForm(p => ({ ...p, industry: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="sponsor-industry-select">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select industry</SelectItem>
                    {INDUSTRIES.map(ind => (<SelectItem key={ind} value={ind}>{ind}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/60 text-xs mb-1 block">Country</Label>
                <Input placeholder="Country" value={sponsorForm.country} onChange={e => setSponsorForm(p => ({ ...p, country: e.target.value }))} className="bg-white/5 border-white/10 text-white" />
              </div>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded p-3 text-xs text-white/40">
              Sponsor will be registered and auto-approved. Default password: <span className="text-white/60 font-mono">sponsor123</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSponsorDialog(false)} className="border-white/10 text-white/60">Cancel</Button>
            <Button onClick={addSponsor} className="bg-orange-600 hover:bg-orange-700" data-testid="btn-save-sponsor">
              <Building2 className="w-4 h-4 mr-1" /> Register & Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Package Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Gift className="w-5 h-5 text-blue-400" /> Assign Package to {assignCorporate?.company_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-white/[0.03] border border-white/5 rounded p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-white/60">Current Package:</span>
                <span className="text-orange-400 font-medium">{assignCorporate?.current_package || 'None'}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-white/60">Current Budget:</span>
                <span className="text-emerald-400 font-medium">{(assignCorporate?.total_budget || 0).toLocaleString()} KES</span>
              </div>
            </div>

            <div>
              <Label className="text-white/60 text-xs mb-2 block">Select Package to Assign</Label>
              <div className="space-y-2 max-h-[250px] overflow-y-auto">
                {activePackages.length === 0 ? (
                  <p className="text-white/30 text-sm text-center py-4">No active packages. Create one first.</p>
                ) : (
                  activePackages.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setAssignPackageId(p.id)}
                      className={`w-full text-left p-3 rounded border transition-colors ${
                        assignPackageId === p.id
                          ? 'border-blue-500/50 bg-blue-500/10'
                          : 'border-white/5 bg-white/[0.02] hover:border-white/20'
                      }`}
                      data-testid={`select-pkg-${p.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-white font-medium text-sm">{p.package_name}</span>
                        <span className="text-yellow-400 font-bold text-sm">{p.amount?.toLocaleString()} {p.currency_code}</span>
                      </div>
                      <div className="flex gap-1.5 mt-1">
                        {p.offer_type && <span className={`text-[10px] px-1.5 py-0.5 rounded ${OFFER_TYPE_COLORS[p.offer_type] || 'bg-white/10 text-white/60'}`}>{OFFER_TYPE_LABELS[p.offer_type] || p.offer_type}</span>}
                        {p.target_sport && <span className="text-[10px] bg-cyan-500/15 text-cyan-400 px-1.5 py-0.5 rounded">{p.target_sport}</span>}
                        <span className="text-[10px] text-white/30">Fee: {p.platform_fee_percentage}%</span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)} className="border-white/10 text-white/60">Cancel</Button>
            <Button onClick={assignPackage} disabled={!assignPackageId} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-confirm-assign">
              <Gift className="w-4 h-4 mr-1" /> Assign Package
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
